import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { authApi, api } from "./api";
import { track, setUserData } from "./pixels";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const check = useCallback(async () => {
    try {
      const me = await authApi.me();
      setUser(me);
    } catch { setUser(null); }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    check();
  }, [check]);

  // Push logged-in user into ad pixels for Advanced Matching (raises EMQ to 6-8/10).
  useEffect(() => { if (user) setUserData(user); }, [user]);

  const login = async (email, password) => {
    const { token, user } = await authApi.login({ email, password });
    localStorage.setItem("vm_token", token);
    // Fetch full user (with store_credit, referral_code, etc.)
    try {
      const me = await authApi.me();
      setUser(me);
      return me;
    } catch {
      setUser(user);
      return user;
    }
  };
  const signup = async (payload) => {
    const { token, user } = await authApi.signup(payload);
    localStorage.setItem("vm_token", token);
    try {
      const me = await authApi.me();
      setUser(me);
      return me;
    } catch {
      setUser(user);
      return user;
    }
  };
  const logout = async () => {
    localStorage.removeItem("vm_token");
    try { await authApi.logout(); } catch {}
    setUser(null);
  };

  return <AuthCtx.Provider value={{ user, loading, login, signup, logout, refresh: check, setUser }}>{children}</AuthCtx.Provider>;
};

export const useAuth = () => useContext(AuthCtx);

// ---- Cart ----
const CartCtx = createContext(null);
const readCart = () => { try { return JSON.parse(localStorage.getItem("vm_cart") || "[]"); } catch { return []; } };

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(readCart);
  const [open, setOpen] = useState(false);
  const [upsellFor, setUpsellFor] = useState(null); // { product_id } triggers popup
  const { user } = useAuth();
  const syncTimer = useRef(null);

  useEffect(() => { localStorage.setItem("vm_cart", JSON.stringify(items)); }, [items]);

  // Debounced sync for logged-in users (fuels abandoned-cart emails)
  useEffect(() => {
    if (!user) return;
    if (syncTimer.current) clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => {
      api.post("/cart/sync", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, variant: i.variant || null, bundle_id: i.bundle_id || null })),
      }).catch(() => {});
    }, 1500);
    return () => clearTimeout(syncTimer.current);
  }, [items, user]);

  const add = (product, quantity = 1, variant = null, bundle_id = null, openDrawer = true) => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.product_id === product.product_id
        && JSON.stringify(i.variant || {}) === JSON.stringify(variant || {})
        && (i.bundle_id || null) === (bundle_id || null));
      if (idx >= 0) { const cp = [...prev]; cp[idx] = { ...cp[idx], quantity: cp[idx].quantity + quantity }; return cp; }
      return [...prev, { product_id: product.product_id, title: product.title, image: (product.images||[])[0], unit_price: product.price, quantity, variant, bundle_id }];
    });
    // Only show upsell modal on the FIRST add of a product (not for bundles/upsell chains)
    if (openDrawer && !bundle_id) setUpsellFor({ product_id: product.product_id, title: product.title });
    else if (openDrawer) setOpen(true);
    track("AddToCart", {
      product_id: product.product_id,
      value: (product.price || 0) * quantity,
      currency: "USD",
      items: [{ id: product.product_id, quantity, price: product.price || 0 }],
    });
  };
  const update = (idx, quantity) => setItems((prev) => prev.map((it, i) => i === idx ? { ...it, quantity: Math.max(1, quantity) } : it));
  const remove = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const clear = () => setItems([]);
  const count = items.reduce((a, b) => a + b.quantity, 0);
  const subtotal = items.reduce((a, b) => a + b.quantity * b.unit_price, 0);

  return <CartCtx.Provider value={{ items, add, update, remove, clear, count, subtotal, open, setOpen, upsellFor, setUpsellFor }}>{children}</CartCtx.Provider>;
};
export const useCart = () => useContext(CartCtx);
